// src/components/layout/Footer.tsx
"use client";

import React from "react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from 'next-intl';
import Logo from "../icons/Logo";
import { useAuth } from "@/context/AuthContext";
import { isRegistrationEnabled } from "@/lib/feature-flags";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const t = useTranslations('footer');
  const { isAuthenticated, logout } = useAuth();

  return (
    <footer className="relative z-10 border-t border-gray-700/50 py-8">
      {/* Glass background */}
      <div className="absolute inset-0 bg-white/5 backdrop-blur-xl" />

      <div className="relative container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
          {/* Logo y descripción */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-md flex items-center justify-center">
                <Logo />
              </div>
              <span className="text-white text-xl font-bold tracking-tight">
                RigCraft
              </span>
            </div>
            <p className="text-gray-400 text-sm text-center md:text-left">
              {t('description')}
            </p>
          </div>

          {/* Resources */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t('resources')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link
                  href="/how-it-works"
                  className="hover:text-white transition-colors"
                >
                  {t('howItWorks')}
                </Link>
              </li>
              <li>
                <Link
                  href="/faq"
                  className="hover:text-white transition-colors"
                >
                  {t('faq')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t('legal')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link
                  href="/terms"
                  className="hover:text-white transition-colors"
                >
                  {t('terms')}
                </Link>
              </li>
              <li>
                <Link
                  href="/privacy"
                  className="hover:text-white transition-colors"
                >
                  {t('privacy')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t('support')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a
                  href="mailto:support@rigcraft.app"
                  className="hover:text-white transition-colors"
                >
                  {t('emailUs')}
                </a>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="hover:text-white transition-colors"
                >
                  {t('contactPage')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Account */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t('account')}</h3>
            <ul className="space-y-2 text-gray-400">
              {isAuthenticated() ? (
                <>
                  <li>
                    <Link
                      href="/profile"
                      className="hover:text-white transition-colors"
                    >
                      {t('myAccount')}
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={logout}
                      className="hover:text-white transition-colors w-full text-center md:text-left"
                    >
                      {t('logout')}
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Link
                      href="/login"
                      className="hover:text-white transition-colors"
                    >
                      {t('login')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/register"
                      className="hover:text-white transition-colors"
                    >
                      {isRegistrationEnabled ? t('register') : t('joinWaitlist')}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center border-t border-gray-800 pt-6">
          <p className="text-sm text-gray-500">
            {t('copyright', { year: currentYear })}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {t('disclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
